/**
 * TopUtilityBar — 본문 패널 맨 위 막대 (2026-10-14 워크스페이스 디자인, 레퍼런스 «⊞ Today … 날짜 · 🔔»)
 *
 *  - 왼쪽: 지금 화면 경로(섹션 › 메뉴)
 *  - 오른쪽: 오늘 날짜·시각 + 알림 종(동그란 단추, 안읽음 점) — 누르면 알림 목록
 *  - 검색은 사이드바(⌘K 팔레트)로, 사용자 메뉴는 사이드바 바닥 프로필로 옮겼다.
 *
 * 알림 로직(30초 폴링 · 새 알림 토스트 · 읽음 처리)은 예전 그대로다.
 * 높이 56px 유지 — 화면들이 `100vh - 56px` 로 높이를 잡는다.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Bell, LayoutGrid, X } from 'lucide-react';
import { Button } from '../ui/shadcn/button';
import { useAuth } from '../../context/AuthContext';
import { useSystemMode } from '../../context/SystemModeContext';
import { menuSections } from '../../constants/menuData';
import notificationService, { Notification } from '../../services/notifications';
import { findPageMeta } from '../../utils/pageMeta';

// Sprint 7B-9: 새 알림 도착 시 우측 슬라이드 토스트
const ToastStack = styled.div`
  position: fixed; top: 64px; right: 20px; z-index: 1300;
  display: flex; flex-direction: column; gap: 8px;
  pointer-events: none;
`;
const ToastCard = styled.div`
  pointer-events: auto;
  width: 340px; max-width: calc(100vw - 32px);
  background: var(--ws-surface); border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-lg); padding: 12px 14px;
  box-shadow: var(--ws-shadow-md);
  display: flex; flex-direction: column; gap: 4px;
  animation: ws-slide-in 200ms ease;
  @keyframes ws-slide-in {
    from { transform: translateX(20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;
const ToastTitle = styled.div`
  font-size: 13.5px; font-weight: 600; color: var(--ws-ink);
  display: flex; align-items: center; gap: 8px;
`;
const ToastBody = styled.div` font-size: 12.5px; color: var(--ws-ink-3); line-height: 1.45; `;

const NotificationDropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  width: 400px;
  max-height: 520px;
  display: flex;
  flex-direction: column;
  background: var(--ws-surface);
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-xl);
  box-shadow: var(--ws-shadow-md);
  overflow: hidden;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transform: translateY(${({ $open }) => ($open ? '0' : '-4px')});
  transition: opacity 150ms, transform 150ms, visibility 0s ${({ $open }) => ($open ? '0s' : '150ms')};
  z-index: 1000;
  @media (max-width: 640px) {
    width: 90vw;
    max-width: 400px;
  }
`;
const NotifHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--ws-line-soft);
`;
const NotifList = styled.div`
  overflow-y: auto;
  max-height: 420px;
`;
const NotifEmpty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: var(--ws-ink-4);
  font-size: 13px;
`;
const NotifItem = styled.div<{ $read: boolean }>`
  position: relative;
  padding: 12px 16px 12px 28px;
  border-bottom: 1px solid var(--ws-line-soft);
  cursor: pointer;
  &:hover, &:focus-visible { background: var(--ws-subtle); outline: none; }
  &:last-child { border-bottom: none; }
  &::before {
    content: '';
    position: absolute; left: 13px; top: 18px;
    width: 6px; height: 6px; border-radius: 50%;
    background: ${({ $read }) => ($read ? 'transparent' : 'var(--ws-blue)')};
  }
`;

// ─────── Breadcrumb 계산 ───────
const findBreadcrumb = (pathname: string): string => {
  // /hr/* 경로는 HR 섹션으로 라벨 매핑 (S2 이후 확장)
  if (pathname.startsWith('/hr')) {
    const hrMap: Record<string, string> = {
      '/hr': '구성원',
      '/hr/me': '구성원 · 내 프로필',
      '/hr/dashboard': '대시보드',
      '/hr/todo': '내 할 일',
      '/hr/search': '구성원 관리 · 구성원 검색',
      '/hr/org-chart': '구성원 관리 · 조직도',
      '/hr/onboarding': '라이프사이클 · 온·오프보딩',
      '/hr/contracts': '라이프사이클 · 계약 관리',
      '/hr/documents': '라이프사이클 · 문서·증명서',
      '/hr/goals': '성장 & 평가 · 목표·OKR',
      '/hr/reviews': '성장 & 평가 · 성과 리뷰',
      // ⛔ '/hr/tasks' 가 빠져 있어 «구성원» 으로 떨어지고 있었다(기존 결함). Q5 이름 변경과 함께 채운다.
      '/hr/tasks': '성장 & 평가 · 진행 프로젝트',
      '/hr/education': '성장 & 평가 · 교육 관리',
      '/hr/payroll': '보상 & 복지 · 급여',
      '/hr/benefits': '보상 & 복지 · 복지',
      '/hr/insurance': '보상 & 복지 · 보험',
      '/hr/trend': '조직 인사이트 · 조직 트렌드',
      '/hr/reports': '조직 인사이트 · 리포트',
      '/hr/responsibility': '업무분장',
      '/hr/admin': '인사 관리자',
      '/hr/notifications': '관리 · 알림',
    };
    // 가장 길게 매치되는 키 선택
    const best = Object.keys(hrMap)
      .filter((k) => pathname === k || pathname.startsWith(k + '/'))
      .sort((a, b) => b.length - a.length)[0];
    // 표에 없는 하위 화면(예: /hr/approvals)이 «구성원» 으로 떨어지지 않게 메뉴 정의를 쓴다
    const meta = best === '/hr' && pathname !== '/hr' ? findPageMeta(pathname) : null;
    if (meta) return meta.section ? `${meta.section} · ${meta.title}` : meta.title;
    if (best) return hrMap[best];
    return 'HR';
  }

  // ERP: menuSections를 순회
  for (const section of menuSections) {
    for (const menu of section.menus) {
      if (pathname === menu.path || pathname.startsWith(menu.path + '/')) {
        return `${section.sidebarTitle} · ${menu.title}`;
      }
    }
  }
  if (pathname.startsWith('/manage')) return '회사 관리';
  if (pathname.startsWith('/settings')) return '설정';
  if (pathname === '/dashboard' || pathname === '/') return '대시보드';
  return '';
};

// ─────── Component ───────
const TopUtilityBar: React.FC = () => {
  const { state } = useAuth();
  const { mode } = useSystemMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [now, setNow] = useState(() => new Date());
  // Sprint 7B-9: 새 알림 토스트
  const [toasts, setToasts] = useState<Notification[]>([]);
  const lastSeenIdRef = useRef<string | null>(null);
  const isFirstLoadRef = useRef(true);

  const notifRef = useRef<HTMLDivElement>(null);

  const label = useMemo(() => findBreadcrumb(location.pathname), [location.pathname]);
  const crumbs = label ? label.split(' · ') : [];

  // 시각 — 1분마다
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const loadUnread = useCallback(async () => {
    if (!state.user?.id) return;
    try {
      const c = await notificationService.getUnreadCount(state.user.id);
      setUnreadCount(c);
    } catch {
      /* 무시 */
    }
  }, [state.user?.id]);

  const loadNotifs = useCallback(async () => {
    if (!state.user?.id) return;
    try {
      const data = await notificationService.getNotifications({
        user_id: state.user.id,
        limit: 20,
      });
      setNotifications(data);
    } catch {
      /* 무시 */
    }
  }, [state.user?.id]);

  // Sprint 7B-9: 새 알림 감지 + 토스트 — 30초 폴링 + visibility hook
  const checkNewNotifs = useCallback(async () => {
    if (!state.user?.id) return;
    try {
      const list = await notificationService.getNotifications({ user_id: state.user.id, limit: 10 });
      // 첫 로드 시에는 토스트 X, 마지막 ID 기록
      if (isFirstLoadRef.current) {
        lastSeenIdRef.current = list[0]?.id || null;
        isFirstLoadRef.current = false;
        return;
      }
      // 마지막 본 ID 이후의 새 알림만 토스트
      if (lastSeenIdRef.current && list.length > 0) {
        const newOnes: Notification[] = [];
        for (const n of list) {
          if (n.id === lastSeenIdRef.current) break;
          if (!n.is_read) newOnes.push(n);
        }
        if (newOnes.length > 0) {
          setToasts((prev) => [...newOnes.slice(0, 3), ...prev].slice(0, 5));
          // 5초 후 자동 제거
          newOnes.forEach((n) => {
            window.setTimeout(() => {
              setToasts((prev) => prev.filter((t) => t.id !== n.id));
            }, 5000);
          });
        }
      }
      lastSeenIdRef.current = list[0]?.id || lastSeenIdRef.current;
    } catch { /* 무시 */ }
  }, [state.user?.id]);

  useEffect(() => {
    loadUnread();
    checkNewNotifs();
    const t = setInterval(() => {
      loadUnread();
      checkNewNotifs();
    }, 30_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        loadUnread();
        checkNewNotifs();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadUnread, checkNewNotifs]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleNotifToggle = async () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) await loadNotifs();
  };

  const handleNotifClick = async (n: Notification) => {
    if (!state.user?.id) return;
    if (!n.is_read) {
      await notificationService.markAsRead(n.id, state.user.id);
      loadUnread();
      loadNotifs();
    }
    if (n.reference_url) window.open(n.reference_url, '_blank');
    setNotifOpen(false);
  };

  const handleMarkAllRead = async () => {
    if (!state.user?.id) return;
    await notificationService.markAllAsRead(state.user.id);
    loadUnread();
    loadNotifs();
  };

  // Sprint 7B-9: 읽음 숨김 토글
  const [hideRead, setHideRead] = useState(false);
  const visibleNotifs = useMemo(
    () => (hideRead ? notifications.filter((n) => !n.is_read) : notifications),
    [notifications, hideRead],
  );

  const dismissToast = (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id));
  const handleToastClick = async (n: Notification) => {
    if (!state.user?.id) return;
    if (!n.is_read) {
      await notificationService.markAsRead(n.id, state.user.id);
      loadUnread();
    }
    if (n.reference_url) navigate(n.reference_url.replace(/^https?:\/\/[^/]+/, ''));
    dismissToast(n.id);
  };

  const fmtTime = (s: string) => {
    const d = new Date(s);
    const diffM = Math.floor((Date.now() - d.getTime()) / 60_000);
    if (diffM < 1) return '방금 전';
    if (diffM < 60) return `${diffM}분 전`;
    const diffH = Math.floor(diffM / 60);
    if (diffH < 24) return `${diffH}시간 전`;
    return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const dateStr = now.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
  const timeStr = now.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="ws-topbar">
      <nav className="ws-crumb" aria-label="화면 경로">
        <LayoutGrid size={16} strokeWidth={1.7} aria-hidden />
        <span className="ws-mono" style={{ fontSize: 11, letterSpacing: '0.06em', color: 'var(--ws-ink-4)' }}>{mode === 'hr' ? 'HR' : 'ERP'}</span>
        {crumbs.map((c, i) => (
          <React.Fragment key={c}>
            <span className="ws-crumb-sep" aria-hidden>›</span>
            <span className={i === crumbs.length - 1 ? 'ws-crumb-current' : undefined}>{c}</span>
          </React.Fragment>
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      <span className="ws-topbar-meta">{dateStr} · {timeStr}</span>

      {/* Notification */}
      <div ref={notifRef} style={{ position: 'relative' }}>
        <Button
          type="button"
          variant="ghost"
          className="ws-btn ws-btn--icon ws-btn--round"
          onClick={handleNotifToggle}
          aria-label={unreadCount > 0 ? `알림 ${unreadCount}건 안읽음` : '알림'}
          aria-expanded={notifOpen}
          style={{ position: 'relative' }}
        >
          <Bell size={17} strokeWidth={1.7} />
          {unreadCount > 0 && <span className="ws-nav-dot" style={{ top: 8, right: 9 }} aria-hidden />}
        </Button>
        <NotificationDropdown $open={notifOpen}>
          <NotifHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="ws-h2" style={{ fontSize: 15 }}>알림</span>
              {unreadCount > 0 && <span className="ws-nav-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button
                type="button"
                variant="ghost"
                className="ws-btn ws-btn--ghost ws-btn--sm"
                onClick={() => setHideRead((v) => !v)}
                title={hideRead ? '읽은 알림도 표시' : '읽은 알림 숨기기'}
              >
                {hideRead ? '전체 표시' : '읽음 숨김'}
              </Button>
              {unreadCount > 0 && (
                <Button type="button" variant="ghost" className="ws-btn ws-btn--ghost ws-btn--sm" style={{ color: 'var(--ws-blue-ink)' }} onClick={handleMarkAllRead}>
                  모두 읽음
                </Button>
              )}
            </div>
          </NotifHeader>
          <NotifList>
            {visibleNotifs.length === 0 ? (
              <NotifEmpty>{hideRead ? '읽지 않은 알림이 없습니다' : '알림이 없습니다'}</NotifEmpty>
            ) : (
              visibleNotifs.map((n) => (
                <NotifItem
                  key={n.id}
                  $read={n.is_read}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotifClick(n)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotifClick(n); }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ws-ink)', flex: 1 }}>{n.title}</span>
                    <span className="ws-num" style={{ fontSize: 11.5, color: 'var(--ws-ink-4)', whiteSpace: 'nowrap' }}>
                      {fmtTime(n.created_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--ws-ink-3)', marginTop: 4, lineHeight: 1.45 }}>{n.message}</div>
                </NotifItem>
              ))
            )}
          </NotifList>
          <div style={{ padding: 10, borderTop: '1px solid var(--ws-line-soft)' }}>
            <Button
              type="button"
              variant="ghost"
              className="ws-btn"
              style={{ width: '100%' }}
              onClick={() => { setNotifOpen(false); navigate(mode === 'hr' ? '/hr/notifications' : '/notifications'); }}
            >
              알림함 열기
            </Button>
          </div>
        </NotificationDropdown>
      </div>

      {/* Sprint 7B-9: 새 알림 토스트 스택 */}
      <ToastStack>
        {toasts.map((t) => (
          <ToastCard key={t.id} onClick={() => handleToastClick(t)} style={{ cursor: 'pointer' }}>
            <ToastTitle>
              <span className="ws-live-dot ws-tone-blue" aria-hidden />
              <span style={{ flex: 1 }}>{t.title}</span>
              <Button
                type="button"
                variant="ghost"
                className="ws-btn ws-btn--ghost ws-btn--icon ws-btn--sm"
                aria-label="닫기"
                onClick={(e) => { e.stopPropagation(); dismissToast(t.id); }}
                style={{ width: 24, height: 24 }}
              >
                <X size={14} />
              </Button>
            </ToastTitle>
            <ToastBody>{t.message}</ToastBody>
          </ToastCard>
        ))}
      </ToastStack>
    </div>
  );
};

export default TopUtilityBar;
